<template>
    <div class="ls-add-property-view--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading">
            <LsFlowHeader :customer="customer" :from="from" />
            <div class="ls-add-property-view--line" />
            <form novalidate class="ls-add-property-view--form" @submit.prevent="addNewProperty">
                <h4>Add Property</h4>
                <LsGoogleAutocomplete
                    id="autocomplete"
                    ref="autocomplete"
                    class="ls-add-property-view--large-input"
                    :validation="{ required: true }"
                    name="google-address-input"
                    track-id="add_property_view-street"
                    @placechanged="placeChanged"
                    @inputChange="streetTextChanged"
                    @no-results-found="clearPick"
                    @places-unavailable="placesUnavailable"
                />
                <p
                    v-if="addressError"
                    class="ls-add-property-view--address-error"
                    test-id="address-error"
                >
                    {{ addressError }}
                </p>
                <LsInput
                    v-model="unit"
                    class="ls-add-property-view--large-input"
                    track-id="add_property_view-unit"
                    name="ut-input"
                    label="Unit number (Optional)"
                    placeholder="e.g. Apt 123"
                />
                <div class="ls-add-property-view--large-input group">
                    <LsInput
                        :value="city"
                        class="ls-add-property-view--small-input"
                        track-id="add_property_view-city"
                        name="ct-input"
                        label="City"
                        :readonly="true"
                        placeholder="Filled from address"
                    />
                    <LsInput
                        :value="state"
                        class="ls-add-property-view--small-input"
                        track-id="add_property_view-state"
                        name="st-input"
                        label="State"
                        :readonly="true"
                        placeholder="Filled from address"
                    />
                </div>
                <LsInput
                    :value="zip"
                    class="ls-add-property-view--small-input"
                    track-id="add_property_view-zip"
                    name="zp-input"
                    label="Zip"
                    :readonly="true"
                    placeholder="Filled from address"
                />
                <LsInput
                    v-model="zendesk_ticket_id"
                    class="ls-add-property-view--small-input"
                    track-id="add_property_view-zendesk"
                    name="zendesk_ticket_id"
                    label="Zendesk ticket number"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />
                <LsError v-if="isInvalid" class="ls-add-property-view--error" />
                <LsButton
                    class="ls-add-property-view--button"
                    type="submit"
                    track-id="add_property_view-submit"
                    text="Add Property"
                    :loading="isUpdating"
                    :disabled="!canSubmit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsModalService } from 'ls-vue-common';
import { LsGoogleAutocomplete, LsButton } from '@/components/common';
import LsFlowHeader from '@/components/LsFlowHeader/LsFlowHeader.vue';
import { FlowSuccessModal } from '@/modals';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsInput from '@/components/common/LsInput/LsInput.vue';
import LsError from '@/components/common/LsError/LsError.vue';
import Customer from '@/graphql/queries/Customer.gql';
import NewProperty from '@/graphql/mutations/NewProperty.gql';
import { apolloClient } from '@/vue-apollo';

const ADDRESS_FIELDS = ['street', 'city', 'state', 'zip'];

const ADDRESS_MESSAGES = {
    lookupUnavailable: 'Address lookup is unavailable. Try again later.',
    incompletePick: 'Pick a full street address with a house number.',
    noPick: 'Pick an address from the suggestions.',
};

function isFilled(value) {
    return _.trim(_.toString(value)) !== '';
}

export default {
    name: 'AddPropertyView',
    components: {
        LsGoogleAutocomplete,
        LsFlowHeader,
        LsTemplatedLoading,
        LsInput,
        LsButton,
        LsError,
    },
    data() {
        return {
            customer: {},
            // The Google place the user picked. Street, city, state and zip come only from here.
            pickedPlace: null,
            // True once the user typed in the street box or tried to submit.
            addressTouched: false,
            lookupUnavailable: false,
            previousAuthFailure: undefined,
            unit: '',
            zendesk_ticket_id: '',
            loading: 0,
            isInvalid: false,
            isUpdating: false,
            from: {},
        };
    },
    computed: {
        city() {
            return _.get(this.pickedPlace, 'city', '');
        },
        state() {
            return _.get(this.pickedPlace, 'state', '');
        },
        zip() {
            return _.get(this.pickedPlace, 'zip', '');
        },
        hasCompletePick() {
            return (
                !_.isNil(this.pickedPlace) &&
                isFilled(this.pickedPlace.street_number) &&
                _.every(ADDRESS_FIELDS, (field) => isFilled(this.pickedPlace[field]))
            );
        },
        canSubmit() {
            return !this.lookupUnavailable && this.hasCompletePick;
        },
        addressError() {
            if (this.lookupUnavailable) {
                return ADDRESS_MESSAGES.lookupUnavailable;
            }
            if (this.pickedPlace && !this.hasCompletePick) {
                return ADDRESS_MESSAGES.incompletePick;
            }
            if (!this.pickedPlace && this.addressTouched) {
                return ADDRESS_MESSAGES.noPick;
            }
            return '';
        },
    },
    $_veeValidate: {
        validator: 'new',
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            vm.from = from;
        });
    },
    created() {
        // Google calls window.gm_authFailure when it rejects the Maps API key.
        this.previousAuthFailure = window.gm_authFailure;
        window.gm_authFailure = (...args) => {
            this.placesUnavailable();
            if (_.isFunction(this.previousAuthFailure)) {
                this.previousAuthFailure(...args);
            }
        };
    },
    beforeDestroy() {
        window.gm_authFailure = this.previousAuthFailure;
    },
    methods: {
        placeChanged(place) {
            this.pickedPlace = _.pick(place, ['street_number', ...ADDRESS_FIELDS]);
        },
        streetTextChanged({ newVal }) {
            this.addressTouched = true;
            // Any edit that moves the text away from the picked street drops the pick.
            if (this.pickedPlace && newVal !== this.pickedPlace.street) {
                this.clearPick();
            }
        },
        clearPick() {
            this.pickedPlace = null;
        },
        placesUnavailable() {
            this.lookupUnavailable = true;
        },
        async addNewProperty() {
            // Enter can submit the form, so the disabled button is not enough.
            this.addressTouched = true;
            if (!this.canSubmit) {
                this.isInvalid = true;
                return;
            }

            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid || !this.canSubmit) {
                return;
            }

            this.isUpdating = true;
            const { street, city, state, zip } = this.pickedPlace;

            this.$apollo
                .mutate({
                    mutation: NewProperty,
                    variables: {
                        customer_id: _.toInteger(this.customer.id),
                        street,
                        units: this.unit ? [this.unit] : null,
                        city,
                        state,
                        zip,
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                })
                .then(({ data }) => {
                    const { createNewProperty } = data;
                    const id = _.toNumber(this.$route.params.customer_id);
                    const queryData = apolloClient.readQuery({
                        query: Customer,
                        variables: { id },
                    });

                    apolloClient.writeQuery({
                        query: Customer,
                        variables: { id },
                        data: {
                            customer: {
                                ...queryData.customer,
                                properties: [
                                    ...queryData.customer.properties,
                                    createNewProperty.property,
                                ],
                            },
                        },
                    });

                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Property Added - ${createNewProperty.property.street}`,
                            copy: createNewProperty.copy,
                            showAddServiceButton: true,
                            routeOverride: () => {
                                this.$router.replace({
                                    name: 'property',
                                    params: {
                                        customer_id: this.customer.id,
                                        property_id: createNewProperty.property.id,
                                    },
                                });
                            },
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
    apollo: {
        customer: {
            query: Customer,
            variables() {
                return {
                    id: _.toNumber(this.$route.params.customer_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.customer = data.customer;
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
            },
            loadingKey: 'loading',
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-add-property-view--line {
    width: 100%;
    height: 1px;
    border-top: 1px dashed $secondary;
    margin-top: 20px;
}
h4 {
    margin: 0;
    color: $primary;
}
.ls-add-property-view--form {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-top: 20px;
    background-color: $white;
    border-radius: $border-radius;
    box-shadow: $box-shadow;
    padding: 30px 20px;
    width: 760px;
    min-height: 550px;
}

.ls-add-property-view--large-input {
    width: 50%;

    &.group {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
}

.ls-add-property-view--small-input {
    width: 165px;
}

.ls-add-property-view--address-error {
    width: 50%;
    margin: -8px 0 8px;
    color: $danger;
}

.ls-add-property-view--error {
    width: 100%;
}

.ls-add-property-view--button {
    width: 135px;
}
</style>
